import { createContext, useEffect, useState, useRef, useMemo } from "react";
import { initTabLeader } from "infrastructure/tabLeader";
import useAuthentication from "hooks/useAuthentication.hook";
import { createSocketManager, STATUS } from "infrastructure/socket/socketManager";
import { registerGeneralSocketListeners } from "infrastructure/socket/generalSocketListeners";
import { registerChatSocketListeners } from "infrastructure/socket/chatSocketListeners";
import { registerCampaignSocketListeners } from "infrastructure/socket/campaignSocketListeners";
import { openDB } from "db/neoConectaDB";
import { init as initEventBridge } from "infrastructure/eventBridge";
import { emit, on } from "infrastructure/eventHub";
import BACKEND from "consts/server.consts";
import { SyncEngine } from "services/sync";
import { getRepositories } from "repositories/RepositoryRegistry";

const InfrastructureContext = createContext(null);
export default InfrastructureContext;

export const InfrastructureContextProvider = ({ children }) => {
  const { auth, user } = useAuthentication();
  const [db, setDb] = useState();
  const [isLeader, setIsLeader] = useState(undefined);
  const isLeaderRef = useRef(isLeader);
  const leaderInitializedRef = useRef(false);
  const [statusSocket, setStatusSocket] = useState(STATUS.DISCONNECTED);

  const socketManager = useMemo(() => createSocketManager({
    onConnect: () => {
      emit("socket", "connection", "connected", "local");
    },
    onRecovered: () => {
      emit("socket", "connection", "recovered", "local");
    },
    onDisconnect: () => {
      emit("socket", "connection", "losed", "local");
    }
  }), []);

  const repositories = useMemo(() => {
    if (!db) return null;
    return getRepositories(db);
  }, [db])

  const syncEngine = useMemo(() => {
    if (!repositories) return null;
    return SyncEngine(repositories.syncState, {
      isLeader: () => isLeaderRef.current
    });
  }, [repositories, isLeaderRef]);

  useEffect(() => {
    isLeaderRef.current = isLeader;
  }, [isLeader])

  useEffect(() => {
    let cancelled = false;

    // Inicializa la base de datos
    openDB().then((db) => {
      if (!cancelled) {
        setDb(db);
      }
    });

    // Se crea el subscribe para recibir actualización del status del socket
    const unsubSocketStatus = socketManager.subscribeStatus((next) => {
      setStatusSocket((prev) => {
        if (prev === next) return prev;
        
        // Solo la tab principal puede enviar el status del socket
        if (isLeaderRef.current) {
          emit("socket", "status", "updated", "global", {
            status: next
          });
        }

        return next;
      });
    });

    // En caso de que se reciba el status del socket, si la tab es "seguidora" 
    // entones se actualiza el status del socket por el que se recibe
    const unsubReceiveStatus = on("socket", "status", "updated", (event) => {
      if (isLeaderRef.current === false) {      
        setStatusSocket(event.payload.status);
      }
    });

    // En caso de que una tab secundaria solicite el status del socket, se envía solo si es la tab principal
    const unsubSendStatus = on("socket", "status", "request", () => {
      if (isLeaderRef.current) {      
        emit("socket", "status", "updated", "global", {
          status: socketManager.getStatus()
        });
      }
    });

    return () => {
      cancelled = true;
      unsubSocketStatus();
      unsubReceiveStatus();
      unsubSendStatus();
    };
  }, []);

  useEffect(() => {
    if (leaderInitializedRef.current) return;
    if (!db || !auth || !syncEngine || !user?.role) return;

    leaderInitializedRef.current = true;
    initTabLeader({
      onFollower: () => {
        setIsLeader(false);
        // Se solicita al lider el status del socket
        emit("socket", "status", "request", "global");
      },
      onBecomingLeader: async ({ previousLeader }) => {
        setIsLeader(true);

        try {
          const token = await auth.getIdToken(true);
          const socket = await socketManager.connect({
            url: BACKEND.BASE_URL,
            transports: ["websocket"],
            token,
          });
          
          registerGeneralSocketListeners(socket);

          if (["AGENT", "SUPERVISOR"].includes(user.role)) {
            registerCampaignSocketListeners(socket);
            registerChatSocketListeners(socket, repositories, user);
          }

          if (user.role === "ADMIN") {
            registerCampaignSocketListeners(socket);
          }

          emit("infrastructure", "leader", "became", "local", { previousLeader });
          
        } catch (error) {
          console.error("Leader socket init failed", error);
          setIsLeader(false);
        }
      },
      onLosingLeader: () => {
        socketManager.disconnect();
        emit("infrastructure", "leader", "lost", "local");
      }
    });
    initEventBridge();
    
    return () => {
      socketManager.disconnect();
      setIsLeader(false);
    };
  }, [db, auth, syncEngine, user?.role]);

  if (!db || !repositories || !syncEngine) return null;

  return (
    <InfrastructureContext.Provider
      value={{
        db,
        socketManager,
        isLeaderRef,
        isLeader,
        statusSocket,
        syncEngine,
        repositories
      }}
    >
      {children}
    </InfrastructureContext.Provider>
  );
};
